import { formatCalendarDate } from '@lib/datetime'
import { GetTravelSegmentResponse } from '@orbit/api-types'
import React from 'react'
import {
	ColumnContainer,
	ColumnContent,
	ColumnHeader,
	ColumnTitle,
	CustomDayName,
	DateDisplay,
	DayNoteIcon,
	DayNoteText,
	EmptyState,
	EmptyStateText,
	EVENT_DOT_ANCHOR,
	EventContent,
	EventDot,
	EventDotColumn,
	StaticDayNoteRow,
	TimelineEventRow,
} from '../Itinerary/Column/Column.styles'
import { RailConnector } from '../Itinerary/timelineRail.styles'
import TravelSegment from '../Itinerary/TravelSegment'
import { pickSegmentRoute } from '../Itinerary/TravelSegment/segmentDisplay'
import { TRAVEL_CHIP_ANCHOR } from '../Itinerary/TravelSegment/TravelSegment.styles'
import { Day } from '../Itinerary/types'
import ReadOnlyCard from './ReadOnlyCard'

interface ReadOnlyColumnProps {
	day: Day
	travelSegments?: GetTravelSegmentResponse[]
}

const ReadOnlyColumn: React.FC<ReadOnlyColumnProps> = ({ day, travelSegments }) => {
	const getTravelSegmentBetweenEvents = (currentEventId: number, nextEventId: number) => {
		if (!travelSegments) return null
		return travelSegments.find(
			segment =>
				segment.fromEventId === currentEventId &&
				segment.toEventId === nextEventId &&
				segment.tripDayIndex === Number(day.id),
		)
	}

	const sortedEvents = day.events

	return (
		<ColumnContainer>
			<ColumnHeader>
				<ColumnTitle>
					{day.displayName}
					<DateDisplay>{formatCalendarDate(day.date, 'weekday')}</DateDisplay>
					{day.name && <CustomDayName>{day.name}</CustomDayName>}
				</ColumnTitle>
			</ColumnHeader>

			{day.note && (
				<StaticDayNoteRow as="div">
					<DayNoteIcon>📝</DayNoteIcon>
					<DayNoteText>{day.note}</DayNoteText>
				</StaticDayNoteRow>
			)}

			<ColumnContent>
				{sortedEvents.map((event, index) => {
					const nextEvent = sortedEvents[index + 1]
					const travelSegment = nextEvent
						? getTravelSegmentBetweenEvents(event.id, nextEvent.id)
						: null
					// The chip row renders only when the segment has a displayable
					// route — the connector must aim at whichever row actually follows.
					const shownSegment =
						travelSegment && pickSegmentRoute(travelSegment) ? travelSegment : null
					const isLastEvent = index === sortedEvents.length - 1

					return (
						<React.Fragment key={event.id}>
							<TimelineEventRow>
								<EventDotColumn>
									{/* One dash run per gap, from this dot into the next
									    row's anchor (travel chip or next dot). */}
									{!isLastEvent && (
										<RailConnector
											$from={EVENT_DOT_ANCHOR}
											$into={shownSegment ? TRAVEL_CHIP_ANCHOR : EVENT_DOT_ANCHOR}
										/>
									)}
									<EventDot />
								</EventDotColumn>
								<EventContent>
									<ReadOnlyCard event={event} />
								</EventContent>
							</TimelineEventRow>
							{shownSegment && <TravelSegment segment={shownSegment} />}
						</React.Fragment>
					)
				})}

				{sortedEvents.length === 0 && (
					<EmptyState>
						<EmptyStateText>No Events Yet</EmptyStateText>
					</EmptyState>
				)}
			</ColumnContent>
		</ColumnContainer>
	)
}

export default ReadOnlyColumn
